import { isNullOrUndefined as isNOU, EventHandler } from '@syncfusion/ej2-base';
import { getSelectedRange } from '../../common/utils/index';
import * as constants from '../../common/constant';
import { events } from '../../common/constant';
/**
 * Manages all event handlers for the BlockEditor component
 * This class centralizes event handling logic and provides a clean interface
 * for wiring and unwiring events across the editor
 */
export class EventManager {
    /**
     * Creates a new EventHandlerManager instance
     *
     * @param {BlockEditor} editor The parent BlockEditor instance
     */
    constructor(editor) {
        this.editor = editor;
    }
    /**
     * Wires up all global event handlers for the editor
     *
     * @returns {void}
     * @hidden
     */
    wireGlobalEvents() {
        // Document events
        EventHandler.add(document, 'selectionchange', this.handleEditorSelection, this);
        EventHandler.add(document, 'mousedown', this.handleDocumentClickActions, this);
        EventHandler.add(document, 'mousemove', this.handleMouseMoveActions, this);
        EventHandler.add(window, 'resize', this.onResizeHandler, this);
        // Editor events
        EventHandler.add(this.editor.element, 'mouseup', this.handleMouseUpActions, this);
        EventHandler.add(this.editor.element, 'mousedown', this.handleMouseDownActions, this);
        EventHandler.add(this.editor.element, 'input', this.handleEditorInputActions, this);
        EventHandler.add(this.editor.element, 'keydown', this.handleKeydownActions, this);
        EventHandler.add(this.editor.element, 'click', this.handleEditorClickActions, this);
        EventHandler.add(this.editor.element, 'copy', this.clipboardActionHandler, this);
        EventHandler.add(this.editor.element, 'cut', this.clipboardActionHandler, this);
        EventHandler.add(this.editor.element, 'paste', this.clipboardActionHandler, this);
        EventHandler.add(this.editor.blockContainer, 'focus', this.handleEditorFocusActions, this);
        EventHandler.add(this.editor.blockContainer, 'blur', this.handleEditorBlurActions, this);
        this.editor.on(events.destroy, this.destroy, this);
    }
    /**
     * Unwires all global event handlers for the editor
     *
     * @returns {void}
     * @hidden
     */
    unWireGlobalEvents() {
        // Document events
        EventHandler.remove(document, 'selectionchange', this.handleEditorSelection);
        EventHandler.remove(document, 'mousedown', this.handleDocumentClickActions);
        EventHandler.remove(document, 'mousemove', this.handleMouseMoveActions);
        EventHandler.remove(window, 'resize', this.onResizeHandler);
        // Editor events
        EventHandler.remove(this.editor.element, 'mouseup', this.handleMouseUpActions);
        EventHandler.remove(this.editor.element, 'mousedown', this.handleMouseDownActions);
        EventHandler.remove(this.editor.element, 'input', this.handleEditorInputActions);
        EventHandler.remove(this.editor.element, 'keydown', this.handleKeydownActions);
        EventHandler.remove(this.editor.element, 'click', this.handleEditorClickActions);
        EventHandler.remove(this.editor.element, 'copy', this.clipboardActionHandler);
        EventHandler.remove(this.editor.element, 'cut', this.clipboardActionHandler);
        EventHandler.remove(this.editor.element, 'paste', this.clipboardActionHandler);
        EventHandler.remove(this.editor.blockContainer, 'focus', this.handleEditorFocusActions);
        EventHandler.remove(this.editor.blockContainer, 'blur', this.handleEditorBlurActions);
        this.editor.off(events.destroy, this.destroy);
    }
    /**
     * Triggers the block change event in the editor with required args
     *
     * @param {BlockChangedEventArgs} args - The event args
     * @returns {void}
     * @hidden
     */
    triggerBlockChangeEvent(args) {
        this.editor.trigger('blockChanged', args);
    }
    handleEditorSelection() {
        this.editor.intermediate.processActions('selectionchange');
    }
    handleMouseMoveActions(moveEvent) {
        this.editor.intermediate.processActions('mousemove', moveEvent);
    }
    handleEditorInputActions(inputEvent) {
        this.editor.intermediate.processActions('input', inputEvent);
    }
    handleDocumentClickActions(clickEvent) {
        this.editor.intermediate.processActions('documentClick', clickEvent);
    }
    handleEditorClickActions(clickEvent) {
        this.editor.intermediate.processActions('editorClick', clickEvent);
    }
    handleEditorFocusActions(focusEvent) {
        setTimeout(() => {
            const range = getSelectedRange();
            if (!range || !this.editor.blockManager.currentFocusedBlock) {
                return;
            }
            this.editor.trigger('focus', {
                event: focusEvent,
                blockId: this.editor.blockManager.currentFocusedBlock.id,
                selectionRange: [range.startOffset, range.endOffset]
            });
        }, 200);
    }
    handleEditorBlurActions(blurEvent) {
        const inlineTbarPopup = document.querySelector('#' + this.editor.element.id + constants.INLINE_TBAR_POPUP_ID);
        const contextMenuPopup = document.querySelector('#' + this.editor.element.id + constants.BLOCKEDITOR_CONTEXTMENU_ID);
        const shouldPreventBlurAction = (inlineTbarPopup && inlineTbarPopup.contains(blurEvent.relatedTarget))
            || (contextMenuPopup && contextMenuPopup.contains(blurEvent.relatedTarget));
        const block = this.editor.blockManager.currentFocusedBlock;
        if (!shouldPreventBlurAction) {
            this.editor.trigger('blur', {
                event: blurEvent,
                blockId: block ? block.id : ''
            });
        }
    }
    handleKeydownActions(keyEvent) {
        this.editor.intermediate.processActions('keydown', keyEvent);
    }
    handleMouseUpActions(mouseEvent) {
        this.editor.intermediate.processActions('mouseup', mouseEvent);
    }
    handleMouseDownActions(mouseEvent) {
        this.editor.intermediate.processActions('mousedown', mouseEvent);
    }
    clipboardActionHandler(e) {
        this.editor.intermediate.processActions('clipboardAction', e);
    }
    onResizeHandler() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.editor.intermediate.processActions('resize');
        }, 10);
    }
    destroy() {
        this.unWireGlobalEvents();
        if (!isNOU(this.resizeTimer)) {
            clearInterval(this.resizeTimer);
            this.resizeTimer = null;
        }
    }
}
